import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Bell, Mail, Plus, X, Send, Building2, DollarSign, MapPin, Trash2, MessageCircle, TicketIcon, RefreshCw, Database, Copy, Check, Eye, EyeOff, Globe, GitBranch, PlayCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { clearAllCaches, forceRefresh } from '@/utils/serviceWorker';
import TierMigrationsCard from '@/components/admin/TierMigrationsCard';

export default function Settings() {
  const { API } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  
  const [settings, setSettings] = useState({
    admin_emails: [],
    notify_on_partner_created: true,
    notify_on_agreement_signed: true,
    notify_on_customer_created: true,
    notify_on_po_confirmed: true,
    notify_on_circuit_activated: true
  });

  // Vendor PO Settings
  const [vendorSettings, setVendorSettings] = useState({
    vendor_emails: {},
    data_centers: {
      chennai: '',
      bangalore: '',
      mumbai: ''
    },
    price_slabs: []
  });
  const [savingVendor, setSavingVendor] = useState(false);

  // WhatsApp Notification Templates
  const [whatsappTemplates, setWhatsappTemplates] = useState({
    ticket_created: '',
    ticket_assigned: '',
    ticket_updated: '',
    ticket_resolved: '',
    sla_breach_alert: '',
    enabled: false
  });
  const [savingWhatsapp, setSavingWhatsapp] = useState(false);

  // WhatsApp Cloud API Settings
  const [waCloudSettings, setWaCloudSettings] = useState({
    phone_number_id: '',
    business_account_id: '',
    access_token: '',
    app_secret: '',
    verify_token: 'ant_broadband_whatsapp_verify_2024',
    webhook_base_url: '',
    is_configured: false
  });
  const [savingWaCloud, setSavingWaCloud] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [copiedWebhook, setCopiedWebhook] = useState(false);

  // Portal Settings
  const [portalSettings, setPortalSettings] = useState({
    signup_min_amount: 1000,
    signup_enabled: true
  });
  const [savingPortal, setSavingPortal] = useState(false);

  // Minimum wallet top-up per tier
  const [minTopup, setMinTopup] = useState({ master: 20000, sub: 10000, lco: 3000 });
  const [savingMinTopup, setSavingMinTopup] = useState(false);

  // Webhook URL for Facebook - use custom URL if set, otherwise API
  const webhookBaseUrl = waCloudSettings.webhook_base_url || API;
  const webhookUrl = `${webhookBaseUrl}/api/whatsapp/webhook`;

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [notifRes, vendorRes, whatsappRes, waCloudRes, portalRes, minTopupRes] = await Promise.all([
          axios.get(`${API}/notification-settings`),
          axios.get(`${API}/vendor-po/settings`).catch(() => ({ data: {} })),
          axios.get(`${API}/tickets/notification-templates`).catch(() => ({ data: {} })),
          axios.get(`${API}/whatsapp/settings`).catch(() => ({ data: {} })),
          axios.get(`${API}/portal-settings`).catch(() => ({ data: {} })),
          axios.get(`${API}/admin/settings/min-topup`).catch(() => ({ data: {} }))
        ]);
        setSettings(notifRes.data);
        if (vendorRes.data) {
          setVendorSettings({
            vendor_emails: vendorRes.data.vendor_emails || {},
            data_centers: vendorRes.data.data_centers || {
              chennai: 'Nxtra Data Center, 101, Santhome high road Santhome Chennai- 600004',
              bangalore: 'Web werks Data center, Vertex Building, 564, near ITPL Main Road, Pattandur Agrahara, Whitefield, Bengaluru, Karnataka 560066',
              mumbai: 'STT Prabhadevi, Kashinath Dhuru Marg, Dadar West, Prabhadevi, Mumbai, Maharashtra 400028'
            },
            price_slabs: vendorRes.data.price_slabs || [
              { min_bandwidth: 0, max_bandwidth: 512, rate_per_mbps: 47 },
              { min_bandwidth: 513, max_bandwidth: 1024, rate_per_mbps: 40 },
              { min_bandwidth: 1025, max_bandwidth: 2096, rate_per_mbps: 35 },
              { min_bandwidth: 2097, max_bandwidth: 999999, rate_per_mbps: 0, manual_required: true }
            ]
          });
        }
        if (whatsappRes.data) {
          setWhatsappTemplates({
            ticket_created: whatsappRes.data.ticket_created || '',
            ticket_assigned: whatsappRes.data.ticket_assigned || '',
            ticket_updated: whatsappRes.data.ticket_updated || '',
            ticket_resolved: whatsappRes.data.ticket_resolved || '',
            sla_breach_alert: whatsappRes.data.sla_breach_alert || '',
            enabled: whatsappRes.data.enabled || false
          });
        }
        if (waCloudRes.data) {
          setWaCloudSettings({
            phone_number_id: waCloudRes.data.phone_number_id || '',
            business_account_id: waCloudRes.data.business_account_id || '',
            access_token: '',  // Don't show existing token, only placeholder
            app_secret: '',    // Don't show existing secret
            verify_token: waCloudRes.data.verify_token || 'ant_broadband_whatsapp_verify_2024',
            webhook_base_url: waCloudRes.data.webhook_base_url || '',
            is_configured: waCloudRes.data.is_configured || false
          });
        }
        if (portalRes.data) {
          setPortalSettings({
            signup_min_amount: portalRes.data.signup_min_amount ?? 1000,
            signup_enabled: portalRes.data.signup_enabled ?? true
          });
        }
        if (minTopupRes.data) {
          setMinTopup({
            master: minTopupRes.data.master ?? 20000,
            sub: minTopupRes.data.sub ?? 10000,
            lco: minTopupRes.data.lco ?? 3000,
          });
        }
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [API]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/notification-settings`, settings);
      toast.success('Notification settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveVendorSettings = async () => {
    setSavingVendor(true);
    try {
      await axios.put(`${API}/vendor-po/settings`, vendorSettings);
      toast.success('Vendor PO settings saved');
    } catch (error) {
      toast.error('Failed to save vendor settings');
    } finally {
      setSavingVendor(false);
    }
  };

  const handleSaveWhatsappTemplates = async () => {
    setSavingWhatsapp(true);
    try {
      await axios.put(`${API}/tickets/notification-templates`, whatsappTemplates);
      toast.success('WhatsApp notification templates saved');
    } catch (error) {
      toast.error('Failed to save WhatsApp templates');
    } finally {
      setSavingWhatsapp(false);
    }
  };

  const handleSaveWaCloudSettings = async () => {
    setSavingWaCloud(true);
    try {
      const updateData = {};
      if (waCloudSettings.phone_number_id) updateData.phone_number_id = waCloudSettings.phone_number_id;
      if (waCloudSettings.business_account_id) updateData.business_account_id = waCloudSettings.business_account_id;
      if (waCloudSettings.access_token) updateData.access_token = waCloudSettings.access_token;
      if (waCloudSettings.app_secret) updateData.app_secret = waCloudSettings.app_secret;
      if (waCloudSettings.verify_token) updateData.verify_token = waCloudSettings.verify_token;
      updateData.webhook_base_url = waCloudSettings.webhook_base_url || '';
      
      await axios.put(`${API}/whatsapp/settings`, updateData);
      toast.success('WhatsApp Cloud API settings saved');
      setWaCloudSettings(prev => ({ ...prev, is_configured: true, access_token: '', app_secret: '' }));
    } catch (error) {
      toast.error('Failed to save WhatsApp Cloud API settings');
    } finally {
      setSavingWaCloud(false);
    }
  };

  const handleSavePortalSettings = async () => {
    setSavingPortal(true);
    try {
      await axios.put(`${API}/portal-settings`, portalSettings);
      toast.success('Portal settings saved');
    } catch (error) {
      toast.error('Failed to save portal settings');
    } finally {
      setSavingPortal(false);
    }
  };

  const handleSaveMinTopup = async () => {
    setSavingMinTopup(true);
    try {
      await axios.put(`${API}/admin/settings/min-topup`, minTopup);
      toast.success('Minimum top-up amounts saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save minimum top-up amounts');
    } finally {
      setSavingMinTopup(false);
    }
  };

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopiedWebhook(true);
    toast.success('Webhook URL copied to clipboard');
    setTimeout(() => setCopiedWebhook(false), 2000);
  };

  const handleAddEmail = () => {
    if (!newEmail || !newEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (settings.admin_emails.includes(newEmail)) {
      toast.error('Email already exists');
      return;
    }
    setSettings({
      ...settings,
      admin_emails: [...settings.admin_emails, newEmail]
    });
    setNewEmail('');
  };

  const handleRemoveEmail = (email) => {
    setSettings({
      ...settings,
      admin_emails: settings.admin_emails.filter(e => e !== email)
    });
  };

  const handleTestEmail = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    setTestingEmail(true);
    try {
      await axios.post(`${API}/notification-settings/test`, { email: testEmail });
      toast.success(`Test email sent to ${testEmail}`);
    } catch (error) {
      toast.error('Failed to send test email. Check SMTP configuration.');
    } finally {
      setTestingEmail(false);
    }
  };

  const updatePriceSlab = (index, field, value) => {
    const newSlabs = [...vendorSettings.price_slabs];
    newSlabs[index] = { ...newSlabs[index], [field]: field === 'manual_required' ? value : Number(value) };
    setVendorSettings({ ...vendorSettings, price_slabs: newSlabs });
  };

  const addPriceSlab = () => {
    const lastSlab = vendorSettings.price_slabs[vendorSettings.price_slabs.length - 1];
    const newSlab = {
      min_bandwidth: (lastSlab?.max_bandwidth || 0) + 1,
      max_bandwidth: (lastSlab?.max_bandwidth || 0) + 500,
      rate_per_mbps: 30,
      manual_required: false
    };
    setVendorSettings({ ...vendorSettings, price_slabs: [...vendorSettings.price_slabs, newSlab] });
  };

  const removePriceSlab = (index) => {
    if (vendorSettings.price_slabs.length <= 1) {
      toast.error('At least one price slab is required');
      return;
    }
    const newSlabs = vendorSettings.price_slabs.filter((_, i) => i !== index);
    setVendorSettings({ ...vendorSettings, price_slabs: newSlabs });
  };

  const notificationEvents = [
    { key: 'notify_on_partner_created', label: 'New Partner Created', description: 'When a new MSO/IPTV partner is onboarded' },
    { key: 'notify_on_agreement_signed', label: 'Agreement Signed', description: 'When a partner signs the MSO agreement' },
    { key: 'notify_on_customer_created', label: 'New Customer Created', description: 'When a new corporate customer is added' },
    { key: 'notify_on_po_confirmed', label: 'Purchase Order Confirmed', description: 'When a customer PO is confirmed' },
    { key: 'notify_on_circuit_activated', label: 'Circuit Activated', description: 'When a circuit is activated with delivery details' },
  ];

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h2 className="text-3xl font-semibold text-slate-900 tracking-tight">Settings</h2>
        <p className="text-slate-600 mt-1">Configure system settings and notifications</p>
      </div>

      <Tabs defaultValue="notifications" className="space-y-6">
        <TabsList>
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Bell className="h-4 w-4" /> Notifications
          </TabsTrigger>
          <TabsTrigger value="whatsapp" className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </TabsTrigger>
          <TabsTrigger value="vendor-po" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" /> Vendor PO Settings
          </TabsTrigger>
          <TabsTrigger value="portal" className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" /> Portal
          </TabsTrigger>
          <TabsTrigger value="system" className="flex items-center gap-2">
            <Database className="h-4 w-4" /> System
          </TabsTrigger>
        </TabsList>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-6">
          {/* Admin Email Recipients */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Admin Email Recipients
              </CardTitle>
              <CardDescription>
                Email addresses that will receive admin notifications for all events
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter email address"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddEmail()}
                  className="flex-1"
                  data-testid="add-email-input"
                />
                <Button onClick={handleAddEmail} variant="outline">
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </div>
              
              {settings.admin_emails.length === 0 ? (
                <div className="text-sm text-slate-500 py-4 text-center border rounded-lg border-dashed">
                  No admin emails configured. Add email addresses to receive notifications.
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {settings.admin_emails.map((email) => (
                    <Badge key={email} variant="secondary" className="py-1.5 px-3 text-sm">
                      {email}
                      <button
                        onClick={() => handleRemoveEmail(email)}
                        className="ml-2 hover:text-red-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notification Events */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notification Events
              </CardTitle>
              <CardDescription>
                Choose which events should trigger email notifications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {notificationEvents.map((event) => (
                <div key={event.key} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50">
                  <div>
                    <p className="font-medium text-slate-900">{event.label}</p>
                    <p className="text-sm text-slate-500">{event.description}</p>
                  </div>
                  <Switch
                    checked={settings[event.key]}
                    onCheckedChange={(checked) => setSettings({ ...settings, [event.key]: checked })}
                    data-testid={`toggle-${event.key}`}
                  />
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Test Email */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5" />
                Test Email Configuration
              </CardTitle>
              <CardDescription>
                Send a test email to verify your SMTP settings are working correctly
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter email to send test"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  className="flex-1"
                  data-testid="test-email-input"
                />
                <Button 
                  onClick={handleTestEmail} 
                  disabled={testingEmail}
                  variant="outline"
                >
                  {testingEmail ? 'Sending...' : 'Send Test'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="save-settings-btn"
            >
              {saving ? 'Saving...' : 'Save Notification Settings'}
            </Button>
          </div>
        </TabsContent>

        {/* WhatsApp Notification Templates Tab */}
        <TabsContent value="whatsapp" className="space-y-6">
          {/* WhatsApp Cloud API Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-green-600" />
                WhatsApp Cloud API Settings
              </CardTitle>
              <CardDescription>
                Configure your official Meta WhatsApp Cloud API credentials. 
                {waCloudSettings.is_configured && (
                  <Badge variant="outline" className="ml-2 bg-green-50 text-green-700 border-green-300">
                    <Check className="h-3 w-3 mr-1" /> Configured
                  </Badge>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Production Domain URL */}
              <div className="space-y-2">
                <Label>Production Domain URL</Label>
                <Input
                  placeholder="https://crm.antbroadband.com"
                  value={waCloudSettings.webhook_base_url}
                  onChange={(e) => setWaCloudSettings({ ...waCloudSettings, webhook_base_url: e.target.value })}
                  data-testid="webhook-base-url-input"
                />
                <p className="text-xs text-slate-500">
                  Your production domain (leave empty to use current app URL)
                </p>
              </div>

              {/* Webhook URL */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
                <Label className="text-blue-800 font-medium">Webhook URL (Copy to Facebook)</Label>
                <div className="flex gap-2">
                  <Input
                    value={webhookUrl}
                    readOnly
                    className="bg-white font-mono text-sm"
                    data-testid="webhook-url-input"
                  />
                  <Button
                    variant="outline"
                    onClick={copyWebhookUrl}
                    className="shrink-0"
                    data-testid="copy-webhook-btn"
                  >
                    {copiedWebhook ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-blue-700">
                  Use this URL in Meta Business Suite &gt; WhatsApp &gt; Configuration &gt; Webhook
                </p>
              </div>

              {/* Verify Token */}
              <div className="space-y-2">
                <Label>Verify Token</Label>
                <div className="flex gap-2">
                  <Input
                    value={waCloudSettings.verify_token}
                    readOnly
                    className="font-mono text-sm"
                    data-testid="verify-token-input"
                  />
                  <Button
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(waCloudSettings.verify_token);
                      toast.success('Verify token copied');
                    }}
                    className="shrink-0"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  Use this token when configuring webhook in Facebook
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {/* Phone Number ID */}
                <div className="space-y-2">
                  <Label>Phone Number ID</Label>
                  <Input
                    placeholder="e.g., 123456789012345"
                    value={waCloudSettings.phone_number_id}
                    onChange={(e) => setWaCloudSettings({ ...waCloudSettings, phone_number_id: e.target.value })}
                    data-testid="phone-number-id-input"
                  />
                  <p className="text-xs text-slate-500">Found in Meta Business Suite &gt; WhatsApp &gt; API Setup</p>
                </div>

                {/* Business Account ID */}
                <div className="space-y-2">
                  <Label>Business Account ID</Label>
                  <Input
                    placeholder="e.g., 123456789012345"
                    value={waCloudSettings.business_account_id}
                    onChange={(e) => setWaCloudSettings({ ...waCloudSettings, business_account_id: e.target.value })}
                    data-testid="business-account-id-input"
                  />
                  <p className="text-xs text-slate-500">WhatsApp Business Account ID from API Setup</p>
                </div>
              </div>

              {/* Access Token */}
              <div className="space-y-2">
                <Label>Permanent Access Token</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      type={showToken ? "text" : "password"}
                      placeholder={waCloudSettings.is_configured ? "••••••••••••••• (already set, enter new to change)" : "Paste your permanent access token"}
                      value={waCloudSettings.access_token}
                      onChange={(e) => setWaCloudSettings({ ...waCloudSettings, access_token: e.target.value })}
                      className="pr-10"
                      data-testid="access-token-input"
                    />
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setShowToken(!showToken)}
                    className="shrink-0"
                  >
                    {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  Generate from Meta Business Suite &gt; System Users &gt; Generate Token
                </p>
              </div>

              {/* App Secret */}
              <div className="space-y-2">
                <Label>App Secret (Optional)</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      type={showSecret ? "text" : "password"}
                      placeholder={waCloudSettings.is_configured ? "••••••••• (already set)" : "For webhook signature verification"}
                      value={waCloudSettings.app_secret}
                      onChange={(e) => setWaCloudSettings({ ...waCloudSettings, app_secret: e.target.value })}
                      className="pr-10"
                      data-testid="app-secret-input"
                    />
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setShowSecret(!showSecret)}
                    className="shrink-0"
                  >
                    {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  Found in Meta App Dashboard &gt; Settings &gt; Basic &gt; App Secret
                </p>
              </div>

              {/* Save Button */}
              <div className="flex justify-end pt-2">
                <Button 
                  onClick={handleSaveWaCloudSettings} 
                  disabled={savingWaCloud}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="save-wa-cloud-settings-btn"
                >
                  {savingWaCloud ? 'Saving...' : 'Save WhatsApp Cloud Settings'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Interakt Notification Templates */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                Interakt WhatsApp Templates (Ticketing)
              </CardTitle>
              <CardDescription>
                Configure Interakt WhatsApp template names for ticket notifications.
                Create these templates in your Interakt Dashboard first.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-base font-medium">Enable WhatsApp Notifications</Label>
                  <p className="text-sm text-slate-500">Send ticket updates via WhatsApp</p>
                </div>
                <Switch
                  checked={whatsappTemplates.enabled}
                  onCheckedChange={(checked) => setWhatsappTemplates({ ...whatsappTemplates, enabled: checked })}
                />
              </div>

              <div className="grid gap-4">
                {/* Ticket Created */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <TicketIcon className="h-4 w-4 text-blue-500" />
                    Ticket Created Template
                  </Label>
                  <Input
                    placeholder="e.g., ticket_created"
                    value={whatsappTemplates.ticket_created}
                    onChange={(e) => setWhatsappTemplates({ ...whatsappTemplates, ticket_created: e.target.value })}
                  />
                  <p className="text-xs text-slate-500">
                    Variables: ticket_number, subject, priority, portal_url
                  </p>
                </div>

                {/* Ticket Assigned */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <TicketIcon className="h-4 w-4 text-amber-500" />
                    Ticket Assigned Template
                  </Label>
                  <Input
                    placeholder="e.g., ticket_assigned"
                    value={whatsappTemplates.ticket_assigned}
                    onChange={(e) => setWhatsappTemplates({ ...whatsappTemplates, ticket_assigned: e.target.value })}
                  />
                  <p className="text-xs text-slate-500">
                    Variables: ticket_number, staff_name, portal_url
                  </p>
                </div>

                {/* Ticket Updated */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <TicketIcon className="h-4 w-4 text-purple-500" />
                    Ticket Updated Template
                  </Label>
                  <Input
                    placeholder="e.g., ticket_updated"
                    value={whatsappTemplates.ticket_updated}
                    onChange={(e) => setWhatsappTemplates({ ...whatsappTemplates, ticket_updated: e.target.value })}
                  />
                  <p className="text-xs text-slate-500">
                    Variables: ticket_number, update_message, status, portal_url
                  </p>
                </div>

                {/* Ticket Resolved */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <TicketIcon className="h-4 w-4 text-emerald-500" />
                    Ticket Resolved Template
                  </Label>
                  <Input
                    placeholder="e.g., ticket_resolved"
                    value={whatsappTemplates.ticket_resolved}
                    onChange={(e) => setWhatsappTemplates({ ...whatsappTemplates, ticket_resolved: e.target.value })}
                  />
                  <p className="text-xs text-slate-500">
                    Variables: ticket_number, resolution_note, portal_url
                  </p>
                </div>

                {/* SLA Breach Alert */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <TicketIcon className="h-4 w-4 text-red-500" />
                    SLA Breach Alert Template (Staff)
                  </Label>
                  <Input
                    placeholder="e.g., sla_breach_alert"
                    value={whatsappTemplates.sla_breach_alert}
                    onChange={(e) => setWhatsappTemplates({ ...whatsappTemplates, sla_breach_alert: e.target.value })}
                  />
                  <p className="text-xs text-slate-500">
                    Variables: ticket_number, customer_name, breach_type
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button 
              onClick={handleSaveWhatsappTemplates} 
              disabled={savingWhatsapp}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {savingWhatsapp ? 'Saving...' : 'Save WhatsApp Settings'}
            </Button>
          </div>
        </TabsContent>

        {/* Vendor PO Settings Tab */}
        <TabsContent value="vendor-po" className="space-y-6">
          {/* Vendor Email */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Vendor Email Addresses
              </CardTitle>
              <CardDescription>
                Email addresses where approved POs will be sent
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>GTPL Vendor Email</Label>
                <Input
                  type="email"
                  value={vendorSettings.vendor_emails?.GTPL || ''}
                  onChange={(e) => setVendorSettings({
                    ...vendorSettings,
                    vendor_emails: { ...vendorSettings.vendor_emails, GTPL: e.target.value }
                  })}
                  placeholder="Enter GTPL vendor email"
                  data-testid="gtpl-vendor-email"
                />
              </div>
            </CardContent>
          </Card>

          {/* Data Center Addresses */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                End A Data Center Addresses
              </CardTitle>
              <CardDescription>
                Configure the data center addresses for PO End A location selection
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Chennai Data Center</Label>
                <Textarea
                  value={vendorSettings.data_centers?.chennai || ''}
                  onChange={(e) => setVendorSettings({
                    ...vendorSettings,
                    data_centers: { ...vendorSettings.data_centers, chennai: e.target.value }
                  })}
                  placeholder="Enter Chennai data center address"
                  rows={2}
                  data-testid="chennai-address"
                />
              </div>
              <div className="space-y-2">
                <Label>Bangalore Data Center</Label>
                <Textarea
                  value={vendorSettings.data_centers?.bangalore || ''}
                  onChange={(e) => setVendorSettings({
                    ...vendorSettings,
                    data_centers: { ...vendorSettings.data_centers, bangalore: e.target.value }
                  })}
                  placeholder="Enter Bangalore data center address"
                  rows={2}
                  data-testid="bangalore-address"
                />
              </div>
              <div className="space-y-2">
                <Label>Mumbai Data Center</Label>
                <Textarea
                  value={vendorSettings.data_centers?.mumbai || ''}
                  onChange={(e) => setVendorSettings({
                    ...vendorSettings,
                    data_centers: { ...vendorSettings.data_centers, mumbai: e.target.value }
                  })}
                  placeholder="Enter Mumbai data center address"
                  rows={2}
                  data-testid="mumbai-address"
                />
              </div>
            </CardContent>
          </Card>

          {/* Price Slabs */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                ARC Price Slabs
              </CardTitle>
              <CardDescription>
                Configure bandwidth-based pricing tiers for ARC calculation (Rate per Mbps per Month)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                {vendorSettings.price_slabs.map((slab, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 border rounded-lg bg-slate-50">
                    <div className="flex-1 grid grid-cols-4 gap-3">
                      <div>
                        <Label className="text-xs text-slate-500">Min Bandwidth (Mbps)</Label>
                        <Input
                          type="number"
                          value={slab.min_bandwidth}
                          onChange={(e) => updatePriceSlab(index, 'min_bandwidth', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Max Bandwidth (Mbps)</Label>
                        <Input
                          type="number"
                          value={slab.max_bandwidth}
                          onChange={(e) => updatePriceSlab(index, 'max_bandwidth', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Rate (₹/Mbps/Month)</Label>
                        <Input
                          type="number"
                          value={slab.rate_per_mbps}
                          onChange={(e) => updatePriceSlab(index, 'rate_per_mbps', e.target.value)}
                          disabled={slab.manual_required}
                          className="mt-1"
                        />
                      </div>
                      <div className="flex items-end gap-2">
                        <div className="flex items-center gap-2 mb-2">
                          <Switch
                            checked={slab.manual_required || false}
                            onCheckedChange={(checked) => updatePriceSlab(index, 'manual_required', checked)}
                          />
                          <Label className="text-xs">Manual Entry</Label>
                        </div>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => removePriceSlab(index)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button variant="outline" onClick={addPriceSlab} className="w-full">
                <Plus className="h-4 w-4 mr-2" /> Add Price Slab
              </Button>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button 
              onClick={handleSaveVendorSettings} 
              disabled={savingVendor}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="save-vendor-settings-btn"
            >
              {savingVendor ? 'Saving...' : 'Save Vendor PO Settings'}
            </Button>
          </div>
        </TabsContent>

        {/* Portal Settings Tab */}
        <TabsContent value="portal" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Self-Signup Portal Settings
              </CardTitle>
              <CardDescription>
                Configure partner self-onboarding portal settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable/Disable Signup */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-base font-medium">Enable Self-Signup</Label>
                  <p className="text-sm text-slate-500">Allow new partners to register via /partner/signup</p>
                </div>
                <Switch
                  checked={portalSettings.signup_enabled}
                  onCheckedChange={(checked) => setPortalSettings({ ...portalSettings, signup_enabled: checked })}
                  data-testid="toggle-signup-enabled"
                />
              </div>

              {/* Minimum Signup Amount */}
              <div className="space-y-2">
                <Label>Minimum Signup Amount (Rs.)</Label>
                <Input
                  type="number"
                  min={0}
                  step={100}
                  placeholder="1000"
                  value={portalSettings.signup_min_amount}
                  onChange={(e) => setPortalSettings({ ...portalSettings, signup_min_amount: Number(e.target.value) })}
                  data-testid="signup-min-amount-input"
                />
                <p className="text-xs text-slate-500">
                  Mandatory wallet top-up amount required during partner registration via Easebuzz.
                  Set to 0 to disable mandatory payment.
                </p>
              </div>

              {/* Info */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700 space-y-1">
                <p className="font-medium">How it works:</p>
                <ul className="list-disc list-inside text-xs space-y-0.5 text-blue-600">
                  <li>Partner fills the signup form at <code className="bg-blue-100 px-1 rounded">/partner/signup</code></li>
                  <li>After registration, they must pay Rs.{portalSettings.signup_min_amount || 0} via Easebuzz</li>
                  <li>On successful payment, account is activated and wallet is credited</li>
                  <li>All active OTT packs become available immediately</li>
                  <li>No admin approval required</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Minimum Top-Up Amounts */}
          <Card data-testid="min-topup-card">
            <CardHeader>
              <CardTitle>Minimum Wallet Top-Up (per Tier)</CardTitle>
              <CardDescription>
                Enforced on every wallet top-up (admin-initiated and portal self-serve).
                Attempts below the tier minimum are rejected before Easebuzz is called.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-3">
              {[
                { key: 'master', label: 'Master Distributor', helper: 'customer_type = partner' },
                { key: 'sub',    label: 'Sub Distributor',    helper: 'customer_type = iptv_distributor' },
                { key: 'lco',    label: 'LCO',                helper: 'customer_type = lco' },
              ].map(({ key, label, helper }) => (
                <div key={key} className="space-y-1.5">
                  <Label htmlFor={`min-topup-${key}`}>{label}</Label>
                  <Input
                    id={`min-topup-${key}`}
                    type="number"
                    min={0}
                    step={100}
                    value={minTopup[key]}
                    onChange={(e) => setMinTopup({ ...minTopup, [key]: Number(e.target.value) })}
                    data-testid={`min-topup-${key}-input`}
                  />
                  <p className="text-xs text-slate-500">{helper}</p>
                </div>
              ))}
            </CardContent>
            <div className="px-6 pb-4 flex justify-end">
              <Button
                onClick={handleSaveMinTopup}
                disabled={savingMinTopup}
                data-testid="save-min-topup-btn"
              >
                {savingMinTopup ? 'Saving…' : 'Save Minimum Top-Up'}
              </Button>
            </div>
          </Card>

          {/* Tier & Hierarchy migrations */}
          <TierMigrationsCard API={API} />

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleSavePortalSettings}
              disabled={savingPortal}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="save-portal-settings-btn"
            >
              {savingPortal ? 'Saving...' : 'Save Portal Settings'}
            </Button>
          </div>
        </TabsContent>

        {/* System Tab - Cache Management */}
        <TabsContent value="system" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Cache Management
              </CardTitle>
              <CardDescription>
                Clear browser cache if you're experiencing issues with stale data
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="font-medium text-amber-800 mb-2">When to clear cache?</h4>
                <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
                  <li>Data not updating after changes</li>
                  <li>Seeing old information after app updates</li>
                  <li>Unexpected behavior or errors</li>
                  <li>After role or permission changes</li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  variant="outline"
                  onClick={async () => {
                    const success = await clearAllCaches();
                    if (success) {
                      toast.success('Cache cleared successfully! Refreshing page...');
                      setTimeout(() => forceRefresh(), 1500);
                    } else {
                      toast.error('Failed to clear cache');
                    }
                  }}
                  className="flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Clear All Cache
                </Button>

                <Button 
                  variant="outline"
                  onClick={() => {
                    forceRefresh();
                  }}
                  className="flex items-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Force Refresh
                </Button>
              </div>

              <div className="text-sm text-slate-500 mt-4">
                <p><strong>Note:</strong> Clearing cache will not log you out. Only browser-stored data will be removed.</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>App Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Version</p>
                  <p className="font-medium">2.0.0</p>
                </div>
                <div>
                  <p className="text-slate-500">Last Updated</p>
                  <p className="font-medium">{new Date().toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-slate-500">Service Worker</p>
                  <p className="font-medium">v2 (No API caching)</p>
                </div>
                <div>
                  <p className="text-slate-500">Cache Strategy</p>
                  <p className="font-medium">Network-first for APIs</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
